import { useQuery } from '@apollo/client';
import { getDataFromTree } from '@apollo/client/react/ssr';
import { NextPage } from 'next';
import { ApolloPageContext, PageProps } from 'helpers/next-page';
// Api
import { GET_EXPERIENCES } from 'api/experiences/queries';
// Types
import {
  GetExperiences,
  GetExperiencesVariables,
} from 'api/experiences/types/GetExperiences';
import {
  ExperienceOrderBy,
  ExperienceStatus,
  SortDirection,
} from 'api/graphql-global-types';
// Helpers
import { getAthleteTabPageProps } from 'helpers/athleteTabPageGuard';
import { getEnvLink } from 'helpers/routes';
import { generateItemListStructuredData } from 'helpers/structured-data';
// Hocks
import withApollo from 'hocs/withApollo';
// Hooks
import {
  useRedirectOnError,
  useRedirectIfNoContent,
  useAthletePageData,
} from 'hooks';
// Layouts
import { getProfileAvatarPreload } from 'helpers/profileAvatar';
import Head from 'layouts/Head/Head';
import Header from 'layouts/Header/Header';
import Main from 'layouts/Main/Main';
import Footer from 'layouts/Footer/Footer';
// Context
import { FiltersProvider } from 'components/common3/PageWithFiltersWrapper/PageWithFiltersContext';
// Components
import AppBannerAndModal from 'components/common3/AppBannerAndModal/AppBannerAndModal';
import Description from 'components/Athlete/Description/Description';
import ExperiencesContainer from 'components/AllExperiences/AllExperiences';
import NotFoundPage from 'components/NotFoundPage/NotFoundPage';

const ExperienceContent = (): JSX.Element | null => {
  const {
    error,
    skip,
    cleanedSlug,
    athleteSlug,
    metaData,
    store,
  } = useAthletePageData();

  const { data: experiencesData } = useQuery<
    GetExperiences,
    GetExperiencesVariables
  >(GET_EXPERIENCES, {
    variables: {
      input: {
        storeIds: [store?.id || ''],
        statuses: [ExperienceStatus.Active],
      },
    },
    skip: !store,
  });

  const experiences = experiencesData?.getExperiences?.entities;

  useRedirectOnError({ error, redirectTo: `/${athleteSlug}` });

  // Redirect to profile home if experiences content is not available
  useRedirectIfNoContent({
    hasContent: store?.hasExperiences ?? false,
    redirectTo: `/${athleteSlug}`,
    skip: !store || skip,
  });

  if (!store) {
    return null;
  }

  const canonicalLink = `${getEnvLink()}/${cleanedSlug}/experiences`;

  const experienceItems =
    experiences?.map((item) => ({
      url: `${getEnvLink()}/${item.store.slug}/experience/${item.slug}`,
    })) || [];

  const structuredData = generateItemListStructuredData(
    `${metaData.name} Experiences`,
    experienceItems
  );

  return (
    <>
      <Head
        title={`${metaData.name} Experiences & Events | MILLIONS`}
        description={`Book exclusive experiences and events with ${metaData.name} on MILLIONS.`}
        image={metaData.avatarUrl}
        imagePreload={getProfileAvatarPreload(metaData.avatarUrl)}
        canonicalLink={canonicalLink}
        robots={!store.hasExperiences ? 'noindex, follow' : undefined}
        structuredData={structuredData}
      />
      <AppBannerAndModal
        when
        variant="profile"
        username={cleanedSlug ?? undefined}
        profileImageUrl={metaData.avatarUrl ?? undefined}
      />
      <Header />

      <Main backgroundColor="background-dim" withoutSidesPadding>
        <Description store={store} />
        {store.hasExperiences && (
          <FiltersProvider
            storesListFilter="hasActiveExperiences"
            searchKey="searchTerm"
            hideContentCreatorsFilter
            hideAthletesFilter
            hideOrganizationsFilter
            hideSports
            hasAthleteSlug
            initialSorting={{
              direction: SortDirection.DESC,
              order: ExperienceOrderBy.createdAt,
            }}
          >
            <ExperiencesContainer
              isPage={false}
              storeSlug={cleanedSlug as string | undefined}
              singleCategory
            />
          </FiltersProvider>
        )}
      </Main>

      <Footer />
    </>
  );
};

const Experience: NextPage<PageProps> = ({ isNotFound }) =>
  isNotFound ? <NotFoundPage /> : <ExperienceContent />;

Experience.getInitialProps = async (
  ctx: ApolloPageContext
): Promise<PageProps> => getAthleteTabPageProps(ctx, 'experiences');

export default withApollo(Experience, { getDataFromTree });
